import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { Button, ButtonTypes } from '../../../common/Button';
import { validateLogin } from '../../../utils/reduxFormUtils';
import { validateAndLoginUser } from '../actions';
import {
  FormLike,
  useSyncFormTranslations
} from '../../../common/useSyncFormTranslations';
import type { LoginForm as LoginFormType } from '../../../utils/reduxFormUtils';

export function LoginForm() {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();
  function onSubmit(formProps: LoginFormType) {
    return dispatch(validateAndLoginUser(formProps));
  }
  const [showPassword, setShowPassword] = useState(false);
  const formRef = useRef<FormLike<LoginFormType> | null>(null);

  const handleVisibility = () => {
    setShowPassword(!showPassword);
  };

  useSyncFormTranslations(formRef, i18n.language);

  return (
    <Form
      fields={['email', 'password']}
      validate={validateLogin}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        submitError,
        submitting,
        modifiedSinceLastSubmit,
        form
      }) => {
        formRef.current = form;

        return (
          <form className="form" onSubmit={handleSubmit}>
            <Field name="email">
              {(field) => (
                <div className="form__field">
                  <label htmlFor="email" className="form__label">
                    {t('LoginForm.Email')}
                  </label>
                  <input
                    className="form__input"
                    aria-label={t('LoginForm.EmailARIA')}
                    type="email"
                    id="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    {...field.input}
                  />
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </div>
              )}
            </Field>
            <Field name="password">
              {(field) => (
                <div className="form__field">
                  <label htmlFor="password" className="form__label">
                    {t('LoginForm.Password')}
                  </label>
                  <div className="form__field__password">
                    <input
                      className="form__input"
                      aria-label={t('LoginForm.PasswordARIA')}
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="current-password"
                      {...field.input}
                    />
                    <button
                      className="form__eye__icon"
                      type="button"
                      onClick={handleVisibility}
                      aria-label={t(
                        showPassword
                          ? 'LoginForm.HidePasswordARIA'
                          : 'LoginForm.ShowPasswordARIA'
                      )}
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible />
                      ) : (
                        <AiOutlineEye />
                      )}
                    </button>
                  </div>
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </div>
              )}
            </Field>
            {submitError && !modifiedSinceLastSubmit && (
              <span className="form-error" aria-live="assertive">
                {t(submitError)}
              </span>
            )}
            <Button type={ButtonTypes.SUBMIT} disabled={submitting}>
              {t('LoginForm.Submit')}
            </Button>
          </form>
        );
      }}
    </Form>
  );
}
